import type React from "react"
import { useForm } from "react-hook-form"
import { Button } from "@/components/ui/button"
import { FieldSet, FieldLegend, FieldGroup } from "@/components/ui/field"
import { Form, FormItem } from "@/components/ui/form"
import { FormInput } from "./form/form-input"
import { FormTextarea } from "./form/form-textarea"
import { FormDatepicker } from "./form/form-datepicker"
import { FormFileUpload } from "./form/form-file-upload"
import { FormCombobox } from "./form/form-combobox"
import { FormMultiSelect } from "./form/form-multiselect"
import type { FormElement, FormSchema } from "./form/types"
import { useEffect, useState, useMemo, useCallback } from "react"
import { Input } from "./ui/input"
import { Label } from "./ui/label"

interface DynamicFormRendererProps {
	schema: FormSchema
	onSubmit?: (data: Record<string, any>) => void
	onChange?: (data: Record<string, any>) => void
}

export function DynamicFormRenderer({ schema, onSubmit, onChange }: DynamicFormRendererProps) {
	const form = useForm({
		defaultValues: {}
	})

	const formData = form.watch()
	const [visibleFields, setVisibleFields] = useState<Set<string>>(new Set())

	const allFieldNames = useMemo(() => {
		const names = new Set<string>()
		const extractFieldNames = (elements: FormElement[]) => {
			elements.forEach((element) => {
				if (element.name) names.add(element.name)
				if (element.children) extractFieldNames(element.children)
			})
		}
		extractFieldNames(schema.elements)
		return names
	}, [schema.elements])

	useEffect(() => {
		setVisibleFields(allFieldNames)
	}, [allFieldNames])

	useEffect(() => {
		if (!schema.conditions) return

		const newVisibleFields = new Set(allFieldNames)
		console.log("Evaluating conditions with formData:", formData)

		schema.conditions.forEach((condition) => {
			const conditionMet = Object.entries(condition.if).every(([key, value]) => formData[key] === value)
			if (!conditionMet) {
				condition.show.forEach((fieldName) => newVisibleFields.delete(fieldName))
			}
		})

		const hasChanged =
			newVisibleFields.size !== visibleFields.size || [...newVisibleFields].some((field) => !visibleFields.has(field))

		if (hasChanged) {
			setVisibleFields(newVisibleFields)
		}
	}, [formData, schema.conditions, allFieldNames, visibleFields])

	const stableOnChange = useCallback(
		(data: Record<string, any>) => {
			onChange?.(data)
		},
		[onChange],
	)

	useEffect(() => {
		stableOnChange(formData)
	}, [formData, stableOnChange])

	const handleFormSubmit = (data: Record<string, any>) => {
		console.log("Form submitted with data:", data)
		onSubmit?.(data)
	}

	const renderElement = (element: FormElement, index: number): React.ReactNode => {
		if (element.name && !visibleFields.has(element.name)) {
			return null
		}

		const key = element.name || `element-${index}`

		switch (element.type) {
			case "input":
				return <FormInput key={key} element={element} form={form} />
			case "textarea":
				return <FormTextarea key={key} element={element} form={form} />
			case "select":
				if (element.attributes?.multiple) {
					return <FormMultiSelect key={key} element={element} form={form} />
				}
				return <FormCombobox key={key} element={element} form={form} />
			case "datepicker":
				return <FormDatepicker key={key} element={element} form={form} />
			case "file":
				return <FormFileUpload key={key} element={element} form={form} />
			case "button":
				return renderButton(element, key)
			case "fieldset":
				return renderFieldset(element, key)
			case "legend":
				return (
					<legend key={key} className="text-sm font-medium">
						{element.ui?.text || element.attributes?.textContent}
					</legend>
				)
			default:
				return null
		}
	}

	const renderButton = (element: FormElement, key: string) => {
		const buttonType = element.attributes?.type || "button"
		const variant = buttonType === "submit" ? "default" : buttonType === "reset" ? "outline" : "secondary"

		return (
			<Button key={key} type={buttonType} variant={variant} {...element.attributes}>
				{element.ui?.text || element.attributes?.textContent || "Button"}
			</Button>
		)
	}

	const renderFieldset = (element: FormElement, key: string) => {
		const legend = element.children?.find((child) => child.type === "legend")
		const otherChildren = element.children?.filter((child) => child.type !== "legend")

		return (
			<FieldSet key={key}>
				{legend && <FieldLegend>{legend.ui?.text || legend.attributes?.textContent}</FieldLegend>}
				<FieldGroup>{otherChildren?.map((child, idx) => renderElement(child, idx))}</FieldGroup>
			</FieldSet>
		)
	}

	const renderLayout = () => {
		if (schema.layout) {
			return schema.layout.map((row, rowIdx) => (
				<div key={rowIdx} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
					{row.map((fieldName) => {
						const element = findElementByName(schema.elements, fieldName)
						return element ? renderElement(element, rowIdx) : null
					})}
				</div>
			))
		}

		return schema.elements.map((element, idx) => renderElement(element, idx))
	}

	const findElementByName = (elements: FormElement[], name: string): FormElement | null => {
		for (const element of elements) {
			if (element.name === name) return element
			if (element.children) {
				const found = findElementByName(element.children, name)
				if (found) return found
			}
		}
		return null
	}

	return (
		<Form {...form}>
			<form
				id={schema.form.id}
				name={schema.form.name}
				onSubmit={form.handleSubmit(handleFormSubmit)}
				className="space-y-6"
			>
				<FormItem>
					<Label htmlFor="integration-name" className="mb-1">Integration Name</Label>
					<Input
						id="integration-name"
						placeholder="Enter integration name"
						value={form.getValues("integrationName") || ""}
						onChange={(e) => form.setValue("integrationName", e.target.value)}
					/>
				</FormItem>
				{renderLayout()}
			</form>
		</Form >
	)
}

